import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Component Imports
import { Loading } from "../../layout";

// Package Imports
import { SvgIcon } from "@mui/material";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { v4 as uuidv4 } from "uuid";

// SVG Imports
import TripOriginIcon from '@mui/icons-material/TripOrigin';
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";

// Util Imports
import { useAlerts } from "@/hooks";

// Service Imports
import {
  useGetAuthOptionsQuery,
} from "@/services/authApi";
import { startOktaSignIn } from "@/services/oktaAuth";

// Hook Imports
import { useAuth, useAuthFlow } from "@/hooks";
import { useAppContext } from "@/context/context";

const OktaSignInButton = ({ clientId }: { clientId: string }) => {
  const handleOktaClick = async () => {
    try {
      await startOktaSignIn({ clientId });
    } catch (error) {
      console.error("Failed to start Okta sign-in:", error);
    }
  };

  return (
    <button
      onClick={handleOktaClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "12px 24px",
        backgroundColor: "#00297A",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: 500,
      }}
    >
      <SvgIcon sx={{ width: "20px" }}>
        <TripOriginIcon />
      </SvgIcon>
      Sign in with Okta
    </button>
  );
};

const Landing = () => {
  // States - START -----------------------------------------
  const location = useLocation();
  const navigate = useNavigate();
  const from = location.state?.from?.pathname || "/";
  const { pushAlertMessage } = useAlerts();
  const [loading, setLoading] = useState(false);
  const { set_admin_status } = useAppContext();
  const { user } = useAuth();
  const { status: authFlowStatus, errorMessage: authFlowError, handleGoogleCredential } =
    useAuthFlow();

  // States - END -------------------------------------------

  // Redux - START -----------------------------------------
  const {
    data: authOptions,
    isLoading: authOptionsLoading,
    isError: authOptionsError,
  } = useGetAuthOptionsQuery();
  // Redux - END -------------------------------------------

  // Methods - START ----------------------------------------
  const handleGoogleSuccess = async (credentialResponse: any) => {
    if (!authOptions?.google_oauth2?.[0]?.google_client_id) {
      pushAlertMessage({
        id: uuidv4(),
        message:
          "Authentication configuration not available for Google. Please try again later.",
        status: 500,
      });
      return;
    }

    try {
      await handleGoogleCredential(credentialResponse.credential);

      pushAlertMessage({
        id: uuidv4(),
        message: "Google authentication successful.",
        status: 200,
      });
      navigate(from, { replace: true });
    } catch (e: any) {
      // If you adjust useAuthFlow to rethrow on failure, you can handle here.
      // If not, use `authFlowError` instead of `e`.
      pushAlertMessage({
        id: uuidv4(),
        message:
          authFlowError ||
          e?.data?.message ||
          e?.message ||
          "Google authentication failed. Please try again.",
        status: 500,
      });
    }
  };

  const handleGoogleError = () => {
    pushAlertMessage({
      id: uuidv4(),
      message: "Google login failed.",
      status: 500,
    });
  };
  // Methods - END ------------------------------------------

  // useEffects - START -------------------------------------
  useEffect(() => {
    if (!authOptionsLoading && authOptionsError) {
      pushAlertMessage({
        id: uuidv4(),
        message:
          "Failed to load authentication options. Please refresh the page.",
        status: 500,
      });
    }
  }, [authOptionsLoading, authOptionsError, pushAlertMessage]);

  useEffect(() => {
    if (user?.admin !== undefined) {
      set_admin_status(user.admin);
    }
  }, [user?.admin, set_admin_status]);

  useEffect(() => {
    setLoading(authOptionsLoading || authFlowStatus === "loading");
  }, [authOptionsLoading, authFlowStatus]);

  useEffect(() => {
    const firstOkta = authOptions?.okta?.[0];
    if (firstOkta?.client_id) {
      localStorage.setItem("okta_client_id", firstOkta.client_id);
    }
  }, [authOptions?.okta]);
  // useEffects - END ---------------------------------------

  return (
    <div
      className="pulse-sidenav-adjusted-container pulse-bg-color--background-800"
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
      }}
    >
      {(authOptionsLoading || authFlowStatus === "loading" || loading) && <Loading />}

      <SvgIcon
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
        children={<LogoSVG className="pulse-color--background-900" />}
      />

      {authOptions?.google_oauth2?.[0]?.google_client_id && (
        <GoogleOAuthProvider
          clientId={authOptions.google_oauth2[0].google_client_id}
        >
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", display: "flex", flexDirection: "row", gap: "10px", justifyContent: "center", alignItems: "center" }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap={false}
            />
            {authOptions?.okta?.[0]?.client_id && (
              <OktaSignInButton clientId={authOptions.okta[0].client_id} />
            )}
          </div>
        </GoogleOAuthProvider>
      )}
    </div>
  );
};

export default Landing;
